import {on, find, removeClass, findIn, removeAttribute, toggleClass, trigger, addClass, setAttribute} from "@elements/dom-utils";
import axios from "axios";
import TerracePlanner from "./index";
import Tab from "bootstrap/js/dist/tab";
import {
    bindStaticListeners,
    updateOutline
} from "./outline/outlineUI";
import {disableLoadingOverlay, enableLoadingOverlay} from "./applicationLoading";
import * as terracePlannerOutlineResize from "./outline/outlineResize";
import * as terracePlannerOutlineInputDependencies from "./outline/outlineInputDependencies";
import * as terracePlannerSurfaceUI from "./surface/surfaceUI";
import * as terracePlannerSubStructureUI from "./subStructure/subStructureUI";
import * as terracePlannerSubstructureSlides from "./subStructure/substructureSlides";
import * as terracePlannerProductUI from "./product/productUI";
import * as terracePlannerPlankPatternUI from "./plankPattern/plankPatternUI";
import * as terracePlannerPlankPatternButtonBars from "./plankPattern/plankPatternButtonBars";
import * as terracePlannerSummaryUI from "./summary/summaryUI";
import {initInScope, cleanUpInScope} from "@elements/init-modules-in-scope";
import {showNotification} from '@elements/alert-notification';
import {translate} from '@elements/translations';
import {updateSurfaceTabText} from "./surface/surfaceUI";
import {subStructureUpdateTab} from "./subStructure/subStructureUI";
import {updateProduct} from "./product/productUI";
import {updatePlankPattern} from "./plankPattern/plankPatternUI";
import {setPdfVisibility} from "./buttonBar";

let backendUrl = window.location.protocol + '//' + window.location.hostname + '/r/terraceplanner/';
let tabData;
let activeTabId;
let tabNodes = {};
let tabPanels = {};
let isLoading;

/**
 * initializes the module, binds eventListeners, loads panels if they are already complete
 *
 * @param initialTabData Anything | Object
 */
export function init (initialTabData) {
    tabData = initialTabData || tabData;

    TerracePlanner.tabConfig = _config.tabConfig;
    TerracePlanner.tabDependencies = _config.tabDependencies;

    /**
     * determine complete status for summary tab, true if all others are complete
     * @type {boolean}
     */
    TerracePlanner.tabConfig.summary.complete = Object.keys(TerracePlanner.tabConfig).reduce(function (accumulator, key) {
        return accumulator && TerracePlanner.tabConfig[key].complete;
    }, true);

    for (let tabId in TerracePlanner.tabConfig) {
        if(!TerracePlanner.tabConfig.hasOwnProperty(tabId)) {continue;}

        if(TerracePlanner.tabConfig[tabId].active) {
            activeTabId = tabId
        }

        tabNodes[tabId] = find('[data-tab-id=' + tabId + ']');
        tabPanels[tabId] = find('#' + tabId);

        on('click', (evt) => {
            switchListener(evt, tabNodes[tabId])
        }, tabNodes[tabId]);

        updateTab(tabId, TerracePlanner.tabConfig[tabId].text, TerracePlanner.tabConfig[tabId].title);

        trigger('terracePlanner.updateTabs', window)


        // if(TerracePlanner.tabConfig[tabId].complete && tabId !== 'summary') {
        //     loadPanelContents(tabId);
        // }
    }

    //todo ?
    if (window.location.hash === '') {
        switchTab('outline');
    }

    on('keydown', (evt) => {
        refreshTabListener(evt);
    }, find('body'));

    if(!onHashChange(null)) {
        if (activeTabId) {
            switchTab(activeTabId);
        }
    }

    // window.onhashchange = onHashChange.bind(this);
}

/**
 * sets custom data for a tab
 *
 * @param name String
 * @param data Anything, Object recommended
 */
export function setTabData (name, data) {
    tabData[name] = data;
}

export function createUrlSearchParams (name) {
    let searchParams = new URLSearchParams(tabData[name]);
    if (tabData[name].dimensions) {
        Object.entries(tabData[name].dimensions).map(([key, value]) => {
            searchParams.delete("dimensions");
            searchParams.append('dimensions['+key+']', value);
        });
    }

    if (tabData[name].plankDirections) {
        searchParams.delete("plankDirections");
        tabData[name].plankDirections.map((pattern) => {
            searchParams.append("plankDirections[]", pattern);
        })
    }

    return searchParams;
}

/**
 * updates the navigation item for a tab with the passed data
 *
 * @param name String
 * @param text String
 * @param title String
 */
export function updateTab (name, text, title) {
    let node = tabNodes[name] || {};
    let config = TerracePlanner.tabConfig[name];

    if('string' === typeof text) {
        findIn('.js-planner__nav-subtitle', node).innerHTML = text;
        config.text = text;
    }

    if('string' === typeof title) {
        findIn('.js-planner__nav-title', node).innerHTML = title;
        config.title = title;
    }
}

/**
 * sets a css class to the tab identified by name
 *
 * @param name
 */
export function visualizeCompleteness (name) {
    removeClass('is-disabled', tabNodes[name]);
}

/**
 * sets a tab to a completed status, uploads the available data
 * switches to the next tab if next is defined
 *
 * @param current String
 * @param next String
 */
export function completeTab(current, next) {
    visualizeCompleteness(current);
    TerracePlanner.tabConfig[current].active = false;
    TerracePlanner.tabConfig[current].complete = true;

    let pendingRequest = axios({method: 'POST', url: backendUrl + current + '/save', data: createUrlSearchParams(current) });

    pendingRequest.then((data) => {
        if('string' === typeof next) {
            switchTab(next);
        }
    });
}

/**
 * sets initialized status for given tab name to false,
 * resulting in reloading the panel upon the next switch.
 * Reapplies the loading animation and removes all tab data and displayed data in the navigation
 *
 * @param name
 */
export function unCompleteTab(name) {
    if(!TerracePlanner.tabConfig[name].initialized) {
        return;
    }

    setTabData(name, {});
    updateTab(name, '');
    completeTab(name);
    TerracePlanner.tabConfig[name].complete = false;
    TerracePlanner.tabConfig[name].initialized = false;

    addClass('is-disabled', tabNodes[name]);
    removeClass('is-complete', tabNodes[name]);
}

/**
 * loads contents for the specified tab panel asynchronously
 *
 * @param name String
 */
export function loadPanelContents (name) {
    let url = backendUrl + name + '/panel';
    let pendingRequest = axios({method: 'GET', url: url });

    isLoading = true;

    enableLoadingOverlay();

    setAttribute('disabled', true, findIn('.js-planner__help-link', TerracePlanner.element));

    return pendingRequest.then((data) => {
        isLoading = false;

        if (data.status === 200) {
            let newEL;

            //todo irgendwann sollten wir alle requests als json object haben
            if (data.data.content) {
                newEL = document.createRange().createContextualFragment(data.data.content)
            } else {
                newEL = document.createRange().createContextualFragment(data.data)
            }

            tabPanels[name].innerHTML = '';
            tabPanels[name].append(newEL);

            cleanUpInScope(tabPanels[name]);
            initInScope(tabPanels[name]);

            TerracePlanner.tabConfig[name].initialized = true;
        }
    });
}

/**
 * switches the active tab, has its contents loaded asynchronously if not already present
 * if the desired tab requires another tab to be completed first, that tab is switched to instead
 *
 * @param name String
 * @param ignoreDependencyAlert bool
 */
export async function switchTab(name, ignoreDependencyAlert, removeComplete) {
    let dependencies = TerracePlanner.tabDependencies[name];

    if (dependencies) {
        for (let index = 0; index < dependencies.length; ++index) {
            let dependency = dependencies[index];

            if(!TerracePlanner.tabConfig[dependency].complete) {
                if(ignoreDependencyAlert === true) {
                    await switchTab(dependency, true);
                } else {
                    showNotification({
                        type: 'light',
                        title: "<span class='icon icon-close text-danger toast-header__icon'></span>" + translate('terracePlanner.tooltip.tabsToBeCompleted').replace('%s', translate('terracePlanner.' + dependency)),
                        closable: true
                    });
                }

                return;
            }

            if(!TerracePlanner.tabConfig[dependency].initialized) {
                await loadPanelContents(dependency);
            }
        }
    }

    if(!TerracePlanner.tabConfig[name].initialized) {
        if (name === 'summary') {
            let interval = setInterval(function(){
                enableLoadingOverlay();

                if (TerracePlanner.tabConfig['outline'].initialized && TerracePlanner.tabConfig['surface'].initialized && TerracePlanner.tabConfig['subStructure'].initialized
                    && TerracePlanner.tabConfig['plankPattern'].initialized && TerracePlanner.tabConfig['product'].initialized && TerracePlanner.tabConfig['product'].complete) {

                    loadPanelContents('summary');

                    clearInterval(interval);
                }

            }, 250);
        } else {
            await loadPanelContents(name);
        }
    }

    let tabInstance = Tab.getOrCreateInstance(tabNodes[name]);
    tabInstance.show();

    Object.entries(TerracePlanner.tabConfig).forEach(([value]) => {
        TerracePlanner.tabConfig[value].active = false;
        removeClass('is-complete', tabNodes[value]);
        removeClass('is-active', tabNodes[value])

        if (TerracePlanner.tabConfig[value].complete) {
            removeClass('is-disabled', tabNodes[value]);

            if (!removeComplete) {
                let dependencies = TerracePlanner.tabDependencies[value];

                if (dependencies) {
                    let completedDependencies = dependencies.filter((item,index) => {
                        return TerracePlanner.tabConfig[item].complete
                    });

                    if (completedDependencies.length === dependencies.length) {
                        addClass('is-complete', tabNodes[value]);
                    } else {
                        addClass('is-disabled', tabNodes[value]);
                    }
                } else {
                    addClass('is-complete', tabNodes[value]);
                }
            }
        }
    });

    addClass('is-active', tabNodes[name]);
    removeClass('is-disabled', tabNodes[name]);

    let top = findIn('.js-planner__content', TerracePlanner.element).getBoundingClientRect().top - document.body.getBoundingClientRect().top - find('.js-nav-container').getBoundingClientRect().height - 20;

    try {
        window.scroll({
            top: top,
            behavior: 'smooth'
        });
    } catch (e) {
        window.scrollTo(0, top);
    }

    activeTabId = name;
    pushHistory();

    TerracePlanner.tabConfig[activeTabId].active = true;
    trigger('switch', tabPanels[activeTabId]);


    if(TerracePlanner.tabConfig[name].initialized) {
        initTabContent(name);
    }
}

/**
 * event listener for navigation items to switch to the related tab
 *
 * @param event
 */
export function switchListener(evt, item) {
    evt.preventDefault();

    let tabName = item.getAttribute('data-tab-id');
    switchTab(tabName);
}

export function initTabContent(currentTab) {
    removeAttribute('hidden', findIn('.js-planner__help-link', TerracePlanner.element));

    if (currentTab === 'outline') {
        if (TerracePlanner.tabConfig['outline'].active === true) {
            terracePlannerPlankPatternButtonBars.init();
            bindStaticListeners(TerracePlanner.element);
            terracePlannerOutlineResize.init();
            terracePlannerOutlineInputDependencies.init();

            setAttribute('data-bs-target', '#outlineHelpModal', findIn('.js-planner__help-link', TerracePlanner.element));
        } else if(TerracePlanner.tabConfig['outline'].complete) {
            updateOutline();
            visualizeCompleteness('outline');
        }
    }
    if (currentTab === 'surface') {
        if (TerracePlanner.tabConfig['surface'].active === true) {
            terracePlannerSurfaceUI.init();
            setAttribute('data-bs-target', '#surfaceHelpModal', findIn('.js-planner__help-link', TerracePlanner.element));
        } else if(TerracePlanner.tabConfig['surface'].complete) {
            updateSurfaceTabText();
            visualizeCompleteness('surface');
        }
    }
    if (currentTab === 'subStructure') {
        if (TerracePlanner.tabConfig['subStructure'].active === true) {
            terracePlannerSubStructureUI.init();
            terracePlannerSubstructureSlides.init();

            setAttribute('data-bs-target', '#substructureHelpModal', findIn('.js-planner__help-link', TerracePlanner.element));
        } else if(TerracePlanner.tabConfig['subStructure'].complete) {
            subStructureUpdateTab(TerracePlanner.tabConfig.subStructure.subStructureSetId);
            visualizeCompleteness('subStructure');
        }
    }
    if (currentTab === 'product') {
        if (TerracePlanner.tabConfig['product'].active === true) {
            terracePlannerProductUI.init();
            setAttribute('data-bs-target', '#materialHelpModal', findIn('.js-planner__help-link', TerracePlanner.element));
        } else if(TerracePlanner.tabConfig['product'].complete) {
            updateProduct(TerracePlanner.tabConfig.product.productId);
            visualizeCompleteness('product');
        }
    }
    if (currentTab === 'plankPattern') {
        if (TerracePlanner.tabConfig['plankPattern'].active === true) {
            terracePlannerPlankPatternUI.init();
            setAttribute('data-bs-target', '#plankPatternHelpModal', findIn('.js-planner__help-link', TerracePlanner.element));
        } else if(TerracePlanner.tabConfig['plankPattern'].complete) {
            updatePlankPattern();
            visualizeCompleteness('plankPattern');
        }
    }
    if (currentTab === 'summary') {
        setAttribute('hidden', true, findIn('.js-planner__help-link', TerracePlanner.element));

        if (TerracePlanner.tabConfig['summary'].active) {
            terracePlannerSummaryUI.init();
        }
    }

    if (TerracePlanner.tabConfig.summary.active) {
        setPdfVisibility(true);
    } else {
        setPdfVisibility(false);
    }

    removeAttribute('disabled', findIn('.js-planner__help-link', TerracePlanner.element));

    disableLoadingOverlay();
}

/**
 * listener to open and close the mobile summary panel
 *
 * @param event MouseEvent
 */
export function mobileSummaryListener (event) {
    toggleClass('is-open', find('.tp-overview-mobile'));
}

/**
 * listener to refresh the active tab when pressing ctrl + shift + F5
 *
 * @param event KeyboardEvent
 */
export function refreshTabListener(event) {
    if(event.ctrlKey && event.shiftKey && 'F5' === event.key) {
        unCompleteTab(activeTabId);
        switchTab(activeTabId);
    }
}

/**
 * pushes the active tab into the browser history to navigate back and forth using the browser's navigation buttons
 */
export function pushHistory() {
    if(-1 === location.hash.indexOf(activeTabId)) {
        history.pushState({tabId: activeTabId}, document.title, location.pathname + '#' + activeTabId);
    }
}

/**
 * listener to switch tabs when the url's hash changes
 * returns true if tab can be switched to
 *
 * @param event
 * @return Boolean
 */
export function onHashChange(event) {
    let tabId = location.hash.replace('#', '');

    if('undefined' !== typeof tabNodes[tabId] && activeTabId !== tabId) {
        switchTab(tabId, true);

        return true;
    }

    return false;
}

/**
 * refreshes the page
 */
export function refreshPage () {
    location.href = window.location.protocol + '//' + window.location.hostname + location.pathname;
}